import type { Lang } from "@iyk/vf-app-config"
import * as React from "react"

import { AudioPlayer } from "./AudioPlayer.ts"
import { AudioPlayerContext } from "./AudioPlayerContext.tsx"
import { useMediaSession } from "./useMediaSession.ts"
import { useAudioElement } from "./useAudioElement.ts"

const AUDIO_ELEMENT_ID = "playlist-audio-player"

export function AudioPlayerProvider({
  children,
  onTrackPlay,
  onTrackEnd,
  onTrackSkip,
  lang
}: AudioPlayerProvider.Props) {
  const [state, setState] = React.useState<{
    currentTrack: AudioPlayer.CurrentTrack | null
  }>({ currentTrack: null })

  const playlistRef = React.useRef<AudioPlayer.Playlist | null>(null)

  const [audioRef, audioControls] = useAudioElement(AUDIO_ELEMENT_ID, {
    onEnded: () => {
      const trackId = state.currentTrack?.id
      if (trackId) onTrackEnd?.(trackId)

      const next = _getAdjacentTrack("next")
      if (next) playNextTrack("auto")
    }
  })

  const _getReleasedTracks = (playlist: AudioPlayer.Playlist) =>
    playlist.tracks.filter(AudioPlayer.Track.$is("Released"))

  const _setCurrentTrackIfChanged = (track: AudioPlayer.ReleasedTrack, playlist: AudioPlayer.Playlist) => {
    const prevTrack = state.currentTrack
    const sameTrack = prevTrack && prevTrack.id === track.id && prevTrack.playlist === playlist

    if (!sameTrack) {
      const index = playlist.tracks.findIndex((t) => t.id === track.id)
      setState({
        currentTrack: {
          id: track.id,
          title: track.title,
          artist: track.artist,
          duration: track.duration,
          index,
          playlist
        }
      })
    }
  }

  const _getAdjacentTrack = (direction: "next" | "previous") => {
    const playlist = playlistRef.current
    const currentTrack = state.currentTrack
    if (!playlist || !currentTrack) return null

    const released = _getReleasedTracks(playlist)
    const currentIndex = released.findIndex((t) => t.id === currentTrack.id)
    if (currentIndex === -1) return null

    const offset = direction === "next" ? 1 : -1
    return released[currentIndex + offset] ?? null
  }

  const _playTrack = (
    track: AudioPlayer.ReleasedTrack,
    playlist: AudioPlayer.Playlist,
    context: AudioPlayerProvider.TrackPlayContext
  ) => {
    playlistRef.current = playlist
    _setCurrentTrackIfChanged(track, playlist)

    audioControls.updateSource(track.playbackLink.url)
    audioControls.play()
    onTrackPlay?.(track.id, context)
  }

  const playPlaylist = (playlist: AudioPlayer.Playlist) => {
    const track = _getReleasedTracks(playlist)[0]
    if (track) {
      _playTrack(track, playlist, "playlist")
    }
  }

  const playTrack = (trackId: string, playlist: AudioPlayer.Playlist) => {
    const track = _getReleasedTracks(playlist).find((t) => t.id === trackId)
    if (track) {
      _playTrack(track, playlist, "manual")
    }
  }

  const playNextTrack = (context: "skip" | "auto" = "skip") => {
    const next = _getAdjacentTrack("next")
    const playlist = playlistRef.current
    const currentTrack = state.currentTrack

    if (next && playlist) {
      if (context === "skip" && currentTrack?.id) {
        onTrackSkip?.(currentTrack.id)
      }
      _playTrack(next, playlist, context)
    } else {
      audioControls.end()
    }
  }

  const playPreviousTrack = () => {
    const prev = _getAdjacentTrack("previous")
    const playlist = playlistRef.current
    const currentTrack = state.currentTrack

    if (!prev || !playlist) {
      audioControls.reset()
      return
    }

    if (currentTrack?.id) onTrackSkip?.(currentTrack.id)

    _playTrack(prev, playlist, "skip")
  }

  const reset = () => {
    audioControls.pause()
    audioControls.reset()
    playlistRef.current = null
    setState({ currentTrack: null })
  }

  useMediaSession(state.currentTrack, state.currentTrack?.playlist.coverImageUrl ?? null, {
    onPlay: audioControls.play,
    onPause: audioControls.pause,
    onNext: playNextTrack,
    onPrevious: playPreviousTrack
  })

  return (
    <AudioPlayerContext.Provider
      value={{
        currentTrack: state.currentTrack,
        trackDuration: audioControls.duration,
        trackIsPlaying: audioControls.isPlaying,
        trackIsBuffering: audioControls.isBuffering,
        trackIsReady: audioControls.isReady,
        getCurrentTrackTime: audioControls.getCurrentTime,
        playPlaylist,
        playTrack,
        playNextTrack,
        playPreviousTrack,
        togglePlayPause: audioControls.togglePlayPause,
        updateCurrentTrackTime: audioControls.seekTo,
        reset,
        lang
      }}
    >
      <audio ref={audioRef} id={AUDIO_ELEMENT_ID} hidden preload="metadata" />
      {children}
    </AudioPlayerContext.Provider>
  )
}

export namespace AudioPlayerProvider {
  export type TrackPlayContext = "playlist" | "auto" | "manual" | "skip"
  export type Props = {
    children: React.ReactNode
    lang: Lang
    onTrackPlay?: (trackId: string, context: TrackPlayContext) => void
    onTrackEnd?: (trackId: string) => void
    onTrackSkip?: (trackId: string) => void
  }
}
